import React, { useEffect, useRef } from 'react';

const StarryBackground = ({
    nebula = 'bottom',   // 'off' | 'bottom' | 'both'
    vignette = 'soft',   // 'off' | 'soft'
}) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { alpha: true });

        let width = 0;
        let height = 0;
        let dpr = 1;

        // Parallax
        let mouseX = 0, mouseY = 0;
        let parallaxTargetX = 0, parallaxTargetY = 0;
        const parallaxEase = 0.06;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Star layers
        const LAYERS = [
            { count: 90, size: [0.6, 1.4], twinkle: [0.7, 1.3], speed: 0.02 },
            { count: 60, size: [0.9, 1.8], twinkle: [0.9, 1.6], speed: 0.035 },
            { count: 35, size: [1.2, 2.2], twinkle: [1.0, 1.9], speed: 0.05 },
        ];

        let stars = [];
        let shootingStars = [];
        let lastTime = 0;

        // Nebula drift
        let nebulaT = Math.random() * 10000;

        const rand = (min, max) => Math.random() * (max - min) + min;

        function resize() {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = Math.floor(width * dpr);
            canvas.height = Math.floor(height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            // rebuild stars
            stars = [];
            LAYERS.forEach((layer, li) => {
                for (let i = 0; i < layer.count; i++) {
                    const r = rand(layer.size[0], layer.size[1]);
                    stars.push({
                        layer: li,
                        x: Math.random() * width,
                        y: Math.random() * height,
                        r,
                        baseAlpha: rand(0.35, 0.9),
                        twinkleSpeed: rand(layer.twinkle[0], layer.twinkle[1]) * 0.5,
                        twinklePhase: Math.random() * Math.PI * 2,
                    });
                }
            });
        }

        function drawNebula(t) {
            if (nebula === 'off') return;

            const drift = prefersReducedMotion ? 0.00015 : 0.0004;
            nebulaT += drift;

            ctx.save();
            ctx.globalCompositeOperation = 'lighter';

            const maxR = Math.max(width, height);

            // Decide which blobs to paint
            const blobs = [];
            if (nebula === 'both') {
                blobs.push(
                    // (Top) very faint � you said this one is distracting, so if re-enabled it's still subtle
                    { x: width * (0.18 + 0.04 * Math.sin(nebulaT * 1.1)), y: height * 0.26, r: maxR * 0.14, c1: 'rgba(73,119,209,0.025)', c2: 'rgba(145,186,255,0.010)' }
                );
            }
            // Bottom-right keeper
            blobs.push(
                { x: width * (0.80 + 0.03 * Math.cos(nebulaT * 0.9)), y: height * 0.72, r: maxR * 0.16, c1: 'rgba(97,147,247,0.028)', c2: 'rgba(35,52,81,0.012)' }
            );

            blobs.forEach(b => {
                const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
                g.addColorStop(0, b.c1);
                g.addColorStop(1, b.c2);
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
                ctx.fill();
            });
            ctx.restore();

            if (vignette !== 'off') {
                const strength = (vignette === 'soft') ? 0.14 : 0.22;
                const vg = ctx.createRadialGradient(
                    width / 2, height / 2, Math.min(width, height) * 0.48,
                    width / 2, height / 2, Math.max(width, height) * 0.85
                );
                vg.addColorStop(0, 'rgba(0,0,0,0)');
                vg.addColorStop(1, `rgba(0,0,0,${strength})`);
                ctx.fillStyle = vg;
                ctx.fillRect(0, 0, width, height);
            }
        }

        function spawnShootingStar() {
            if (Math.random() < 0.25) {
                const fromLeft = Math.random() < 0.5;
                const startX = fromLeft ? rand(-width * 0.2, width * 0.1) : rand(width * 0.9, width * 1.2);
                const startY = rand(-height * 0.1, height * 0.5);
                const angle = fromLeft ? rand(15, 30) : rand(150, 165);
                const speed = prefersReducedMotion ? rand(450, 650) : rand(650, 900);
                const vx = Math.cos((angle * Math.PI) / 180) * speed * (fromLeft ? 1 : -1);
                const vy = Math.sin((angle * Math.PI) / 180) * speed;

                shootingStars.push({ x: startX, y: startY, vx, vy, life: 0, maxLife: rand(0.7, 1.2), width: rand(1.2, 2), len: rand(120, 220) });
            }
        }

        function drawShootingStars(dt) {
            ctx.save();
            ctx.globalCompositeOperation = 'lighter';
            for (let i = shootingStars.length - 1; i >= 0; i--) {
                const s = shootingStars[i];
                s.life += dt;
                s.x += s.vx * dt;
                s.y += s.vy * dt;

                const t = 1 - (s.life / s.maxLife);
                const alpha = Math.max(0, Math.min(1, t));
                if (alpha <= 0) { shootingStars.splice(i, 1); continue; }

                const mag = Math.hypot(s.vx, s.vy);
                const trailX = s.x - (s.vx / mag) * s.len;
                const trailY = s.y - (s.vy / mag) * s.len;

                const grad = ctx.createLinearGradient(trailX, trailY, s.x, s.y);
                grad.addColorStop(0, `rgba(145,186,255,0)`);
                grad.addColorStop(1, `rgba(145,186,255,${0.65 * alpha})`);
                ctx.strokeStyle = grad;
                ctx.lineWidth = s.width;
                ctx.beginPath();
                ctx.moveTo(trailX, trailY);
                ctx.lineTo(s.x, s.y);
                ctx.stroke();

                ctx.fillStyle = `rgba(255,255,255,${0.9 * alpha})`;
                ctx.beginPath();
                ctx.arc(s.x, s.y, s.width + 0.6, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.restore();
        }

        function drawStars(t) {
            ctx.clearRect(0, 0, width, height);

            parallaxTargetX += (mouseX - parallaxTargetX) * parallaxEase;
            parallaxTargetY += (mouseY - parallaxTargetY) * parallaxEase;

            for (let i = 0; i < stars.length; i++) {
                const s = stars[i];
                const layer = LAYERS[s.layer];

                const twinkle = Math.sin((t * s.twinkleSpeed) + s.twinklePhase) * 0.5 + 0.5;
                const a = Math.max(0.15, Math.min(1, s.baseAlpha * (0.7 + twinkle * 0.6)));

                if (!prefersReducedMotion) {
                    s.x += layer.speed;
                    if (s.x > width + 5) s.x = -5;
                }

                const depth = (s.layer + 1);
                const px = s.x + (parallaxTargetX * (depth * 0.02));
                const py = s.y + (parallaxTargetY * (depth * 0.02));

                ctx.fillStyle = `rgba(255,255,255,${a})`;
                ctx.beginPath();
                ctx.arc(px, py, s.r, 0, Math.PI * 2);
                ctx.fill();

                if (s.layer === 2 && Math.random() < 0.02) {
                    ctx.fillStyle = `rgba(145,186,255,${a * 0.18})`;
                    ctx.beginPath();
                    ctx.arc(px, py, s.r * 2.2, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            drawNebula(t);
        }

        function frame(ts) {
            const time = ts / 1000;
            const dt = Math.min(0.033, time - lastTime || 0.016);
            lastTime = time;

            drawStars(time);
            drawShootingStars(dt);

            requestAnimationFrame(frame);
        }

        function onMouseMove(e) {
            const nx = (e.clientX / width) * 2 - 1;
            const ny = (e.clientY / height) * 2 - 1;
            mouseX = nx * 12;
            mouseY = ny * 10;
        }

        const shootTimer = setInterval(() => {
            if (!prefersReducedMotion) spawnShootingStar();
        }, 6000 + Math.random() * 6000);

        window.addEventListener('mousemove', onMouseMove, { passive: true });
        window.addEventListener('resize', resize);
        resize();
        requestAnimationFrame(frame);

        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', resize);
            clearInterval(shootTimer);
        };
    }, [nebula, vignette]);

    return (
        <canvas
            ref={canvasRef}
            className="StarryBackground"
            style={{
                position: 'fixed',
                inset: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 0,
                pointerEvents: 'none',
            }}
            aria-hidden="true"
        />
    );
};

export default StarryBackground;
